import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, LockIcon, MicIcon, ImageIcon, PaperclipIcon, ArrowUpIcon } from 'lucide-react';
import { Logo } from '../Logo';
const EASE = [0.23, 1, 0.32, 1] as const;
interface TalabWindowProps {
  url?: string;
  storeName?: string;
  storeBadge?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * The real Talab surface, restaged at deck scale: browser chrome, the product's
 * white app header with the Talab lockup, and the dotted Discover canvas.
 */
export function TalabWindow({
  url = 'talab.ai',
  storeName,
  storeBadge = 'AI Storefront',
  children,
  className = ''
}: TalabWindowProps) {
  return <div className={`flex flex-col overflow-hidden rounded-[18px] bg-white border border-slate-200/70 ${className}`} style={{
    boxShadow: '0 40px 90px -30px rgba(11,18,32,0.45)'
  }} data-id="element-2353" data-mp="1pv9q6xr8s9u">
      <div className="flex items-center gap-2.5 h-8 px-3.5 shrink-0 bg-slate-50 border-b border-slate-100" data-id="element-2354" data-mp="1w2uvkhsqaipk">
        <div className="flex items-center gap-1.5" data-id="element-2355" data-mp="11qpfst4774nx">
          <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-2356" data-mp="11t1wl2y7z8ze" />
          <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-2357" data-mp="16r3g1ozk5abe" />
          <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-2358" data-mp="rt19d5x8c8px" />
        </div>
        <div className="flex items-center justify-center gap-1.5 mx-auto h-5 w-[190px] rounded-md bg-white border border-slate-200 text-[10px] text-slate-400" data-id="element-2359" data-mp="rv5l2a1dec0r">
          <LockIcon className="w-2.5 h-2.5" data-id="element-2360" data-mp="1kirmpcr1f6kz" />
          <span className="truncate" data-id="element-2361" data-mp="1pgjr368vcl62">{url}</span>
        </div>
      </div>

      <div className="flex items-center justify-between h-12 px-4 shrink-0 bg-white border-b border-slate-100" data-id="element-2362" data-mp="1pfrsdmjz67bk">
        <div className="flex items-center gap-3" data-id="element-2363" data-mp="1apsznzb6vo3h">
          <Logo size="sm" className="scale-[0.85] origin-left" data-id="element-2364" data-mp="1fposr3iclv49" />
          {storeName && <>
              <span className="w-px h-4 bg-slate-200" data-id="element-2365" data-mp="23fj41gn0rbme" />
              <span className="font-display font-bold text-[12px] text-dark-navy" data-id="element-2366" data-mp="286rdgcocmwzk">
                {storeName}
              </span>
            </>}
          <span className="px-2 py-0.5 rounded-full bg-primary-indigo/[0.07] text-[9px] font-semibold uppercase tracking-[0.12em] text-primary-indigo" data-id="element-2367" data-mp="2i2hvvcpsg81n">
            {storeBadge}
          </span>
        </div>
        <div className="flex items-center gap-2" data-id="element-2368" data-mp="290icwev7qtw2">
          <span className="flex items-center gap-1.5 text-[9.5px] text-slate-400" data-id="element-2369" data-mp="243ryrx2yy8da">
            <span className="w-1.5 h-1.5 rounded-full bg-success" data-id="element-2370" data-mp="1z3vs1q2w02u1" />
            Online
          </span>
          <span className="w-6 h-6 rounded-full bg-slate-100" data-id="element-2371" data-mp="1udp47sg78mcu" />
        </div>
      </div>

      <div className="relative flex-1 min-h-0 bg-white" data-id="element-2372" data-mp="1pec96kf6c8z6">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at center, rgba(67,56,202,0.08) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        opacity: 0.5
      }} aria-hidden="true" data-id="element-2373" data-mp="1pevnzs9j2h8z" />
        <div className="relative h-full" data-id="element-2374" data-mp="1bgnyg5etmi7u">{children}</div>
      </div>
    </div>;
}
export function TalabAgentAvatar({
  size = 28
}: {
  size?: number;
}) {
  return <div className="shrink-0 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md" style={{
    width: size,
    height: size
  }} data-id="element-2375" data-mp="3rzye2khpbe5g">
      <SparklesIcon className="text-white" style={{
      width: size * 0.45,
      height: size * 0.45
    }} data-id="element-2376" data-mp="4va4k9tsq1kx" />
    </div>;
}
export function TalabUserBubble({
  children,
  caret = false
}: {
  children: React.ReactNode;
  caret?: boolean;
}) {
  return <div className="flex justify-end" data-id="element-2377" data-mp="398fcmi3bs85j">
      <div className="bg-slate-100 rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[78%] text-[12.5px] leading-relaxed text-dark-navy" data-id="element-2378" data-mp="3dbld193rb543">
        {children}
        {caret && <motion.span className="inline-block w-[1.5px] h-[13px] ml-0.5 -mb-[2px] bg-primary-indigo align-middle" animate={{
        opacity: [1, 1, 0, 0]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-2379" data-mp="2lkxem4tgwdu4" />}
      </div>
    </div>;
}
export function TalabAgentBubble({
  children,
  delay = 0,
  rtl = false
}: {
  children: React.ReactNode;
  delay?: number;
  /** Right-to-left copy (the agent replying in Arabic). */
  rtl?: boolean;
}) {
  return <motion.div className="flex gap-3" initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: EASE,
    delay
  }} dir={rtl ? 'rtl' : undefined} data-id="element-2380" data-mp="2bha9solzc8e9">
      <TalabAgentAvatar data-id="element-2381" data-mp="2ka3rstst9lvt" />
      <div className="flex-1 min-w-0" data-id="element-2382" data-mp="2k8sosw76wi5r">
        <div className={`bg-white rounded-2xl px-4 py-3 shadow-soft border border-slate-100 text-[12.5px] leading-relaxed text-slate-700 ${rtl ? 'rounded-tr-none text-right' : 'rounded-tl-none'}`} data-id="element-2383" data-mp="2z6l1lglcqsg9">
          {children}
        </div>
      </div>
    </motion.div>;
}
export function TalabTypingRow() {
  return <motion.div className="flex gap-3" initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: EASE
  }} data-id="element-2384" data-mp="zv1u76fp5s8p">
      <TalabAgentAvatar data-id="element-2385" data-mp="3gfdywtwfhb71" />
      <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3.5 shadow-soft border border-slate-100 flex items-center gap-1.5" data-id="element-2386" data-mp="3ghqcyvz2f1et">
        {[0, 150, 300].map(delay => <span key={delay} className="w-1.5 h-1.5 rounded-full bg-primary-indigo animate-bounce" style={{
        animationDelay: `${delay}ms`
      }} data-id="element-2387" data-mp="36o9irqv0nkam" />)}
      </div>
    </motion.div>;
}
interface TalabAskBarProps {
  text?: string;
  placeholder?: string;
  caret?: boolean;
  focused?: boolean;
  className?: string;
  /** Single-row composer used when the window is showing a live conversation. */
  compact?: boolean;
}

/** The Talab ask input, matching the Discover composer. */
export function TalabAskBar({
  text = '',
  placeholder = 'Ask Talab anything…',
  caret = false,
  focused = false,
  className = '',
  compact = false
}: TalabAskBarProps) {
  if (compact) {
    return <div className={`relative ${className}`} data-id="element-2388" data-mp="fjmoegful7jg">
        <motion.div className="absolute -inset-[3px] rounded-[26px] bg-gradient-to-r from-primary-indigo/25 to-ai-purple/25 blur-md" animate={{
        opacity: focused ? 1 : 0
      }} transition={{
        duration: 0.35,
        ease: EASE
      }} aria-hidden="true" data-id="element-2389" data-mp="fitrs8alryf1" />
        <div className="relative flex items-center gap-2 bg-white rounded-full border border-slate-100 pl-4 pr-1.5 py-1.5 shadow-[0_6px_20px_rgb(0,0,0,0.07)]" data-id="element-2390" data-mp="1385v4rj91c9f">
          <div className="flex-1 min-w-0 text-[12.5px] truncate" data-id="element-2391" data-mp="tje7gvl81e4b">
            {text ? <span className="text-dark-navy" data-id="element-2392" data-mp="th1i3xd4w1h8">{text}</span> : <span className="text-slate-300" data-id="element-2393" data-mp="ok1c1mgaayq4">{placeholder}</span>}
            {caret && <motion.span className="inline-block w-[1.5px] h-[13px] ml-0.5 -mb-[2px] bg-primary-indigo align-middle" animate={{
            opacity: [1, 1, 0, 0]
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-2394" data-mp="1m1rx47weltsb" />}
          </div>
          <MicIcon className="w-3.5 h-3.5 text-slate-300 shrink-0" data-id="element-2395" data-mp="1tl17a3cqhwif" />
          <PaperclipIcon className="w-3.5 h-3.5 text-slate-300 shrink-0" data-id="element-2396" data-mp="1ygpzflmfcb4p" />
          <span className="w-7 h-7 rounded-full bg-primary-indigo flex items-center justify-center shrink-0" data-id="element-2397" data-mp="23hnomi5uq5fp">
            <ArrowUpIcon className="w-3.5 h-3.5 text-white" data-id="element-2398" data-mp="28fp997ixecta" />
          </span>
        </div>
      </div>;
  }
  return <div className={`relative ${className}`} data-id="element-2399" data-mp="2cg976dbbpkgh">
      <motion.div className="absolute -inset-[3px] rounded-[26px] bg-gradient-to-r from-primary-indigo/25 to-ai-purple/25 blur-md" animate={{
      opacity: focused ? 1 : 0
    }} transition={{
      duration: 0.35,
      ease: EASE
    }} aria-hidden="true" data-id="element-2400" data-mp="2h8aedktmwkzn" />
      <div className="relative bg-white rounded-3xl border border-slate-100 p-1.5 shadow-[0_8px_30px_rgb(0,0,0,0.08)]" data-id="element-2401" data-mp="2r55ci2lzwjde">
        <div className="rounded-[20px] px-4 py-3" data-id="element-2402" data-mp="3hgrl6h52fklr">
          <div className="min-h-[19px] text-[13px] leading-snug" data-id="element-2403" data-mp="3hf6p2n33rtls">
            {text ? <span className="text-dark-navy" data-id="element-2404" data-mp="3chexju6de9i4">{text}</span> : <span className="text-slate-300" data-id="element-2405" data-mp="3qgn08x1qrzr2">{placeholder}</span>}
            {caret && <motion.span className="inline-block w-[1.5px] h-[14px] ml-0.5 -mb-[2px] bg-primary-indigo align-middle" animate={{
            opacity: [1, 1, 0, 0]
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-2406" data-mp="3ljo4yb6cdmra" />}
          </div>
          <div className="flex items-center justify-between mt-3" data-id="element-2407" data-mp="37jlqz015ndhb">
            <span className="text-[8.5px] font-semibold uppercase tracking-[0.18em] text-slate-400" data-id="element-2408" data-mp="32rlq95d9ggjo">
              Find · Buy · Sell · Hire · Rent · Compare
            </span>
            <div className="flex items-center gap-1" data-id="element-2409" data-mp="2a3g0g47co1cz">
              <MicIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-2410" data-mp="2a2wvdo2st7gd" />
              <ImageIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-2411" data-mp="pkvqwmwwe252" />
              <PaperclipIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-2412" data-mp="pjkoorj3p2g2" />
              <span className="ml-1.5 w-7 h-7 rounded-full bg-primary-indigo flex items-center justify-center" data-id="element-2413" data-mp="udouv8kjl6g3">
                <ArrowUpIcon className="w-3.5 h-3.5 text-white" data-id="element-2414" data-mp="gmk1go7rpae7" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}